import { BAOCustomElement } from '@bao/custom-element'
import { eventBus, EVENTS } from '@bao/events'
import { getElement, getElements, gracefullyReplaceNode } from '@bao/utils'

const selectors = {
  addToCartPanel: '[data-product-add-to-cart-panel]',
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockPanel: '[data-product-back-in-stock-panel]',
  backInStockForm: '[data-product-back-in-stock-form]',
  media: '[data-product-media]',
  price: '[data-product-price]',
  stock: '[data-product-stock]',
  wishlistTrigger: '[data-product-wishlist-trigger]',
}

export class ProductInfo extends BAOCustomElement() {
  static get requiredElements () {
    return ['form']
  }

  static get requiredAttributes () {
    return ['data-section', 'data-product-info-id', 'data-url']
  }

  abortController = undefined
  pendingRequestUrl = undefined
  preReplaceProductOptionsCallbacks = []
  postReplacePageCallbacks = []

  checkValidity () {
    super.checkValidity()

    if (this.els.form.element.tagName !== 'PRODUCT-FORM') {
      this.throwError(`this.els.form.element should be a <product-form>`)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onProductOptionChange = this.onProductOptionChange.bind(this)
    this.onProductOptionEntered = this.onProductOptionEntered.bind(this)

    eventBus.addEventListener(
      EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    eventBus.addEventListener(
      EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    eventBus.removeEventListener(
      EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    eventBus.removeEventListener(
      EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {string[]} payload.detail.selectedOptionValues
   */
  onProductOptionEntered ({ detail: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    const optionTarget = event.target.querySelector('[data-option-value-id]')
    if (!optionTarget) return
    if (!optionTarget.dataset.optionValueId) return

    const name = optionTarget.getAttribute('name')
    const selectedOption = this.querySelector(
      `[name="${name}"][selected], [name="${name}"]:checked`,
    )
    if (!selectedOption) return
    if (selectedOption === optionTarget) return

    const selectedOptionId = selectedOption.dataset.optionValueId
    if (!selectedOptionId) return

    const newSelectedOptionValues = selectedOptionValues.map((value) => {
      if (value === selectedOptionId) return optionTarget.dataset.optionValueId

      return value
    })

    const productUrl = optionTarget.dataset.productUrl || this.dataset.url
    fetchProductData(
      this.buildRequestUrlWithParams(productUrl, newSelectedOptionValues),
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {HTMLOptionElement | EventTarget} payload.detail.target
   * @param {string[]} payload.detail.selectedOptionValues
   */
  async onProductOptionChange ({
    detail: { event, target, selectedOptionValues },
  }) {
    if (!this.contains(target)) return
    if (this.dataset.productInfoId !== target.dataset.productInfoId) return

    this.loading = true

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.dataset.url !== productUrl
    const shouldFetchFullPage =
      this.dataset.updateUrl === 'true' && shouldSwapProduct

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.renderProductDetails(productUrl),
    })
  }

  /**
   * @callback fetchProductDetailsCallback
   * @param {Document} html
   */

  /**
   * @param {Object} payload
   * @param {string} payload.requestUrl
   * @param {string} payload.targetId
   * @param {fetchProductDetailsCallback} payload.callback
   */
  async fetchProductDetails ({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const text = await fetchProductData(
        requestUrl,
        this.abortController.signal,
      )

      this.pendingRequestUrl = undefined

      const html = new DOMParser().parseFromString(text, 'text/html')

      callback(html)

      getElement(`#${targetId}`)?.focus()
    } catch (e) {
      if (e.name === 'AbortError') {
        console.info('Fetch aborted by user')
      } else {
        console.error(e)
      }
    }
  }

  /**
   * @param {string} productUrl
   * @param {boolean} updateFullPage
   */
  handleSwapProduct (productUrl, updateFullPage = false) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      this.updateURL(productUrl, variant?.id)

      if (updateFullPage) {
        document.querySelector('head title').innerHTML =
          html.querySelector('head title').innerHTML

        gracefullyReplaceNode(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      } else {
        gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      }

      // reviews.io only initialises rating snippets on window load
      window.reinitialiseRatingSnippet?.()
    }
  }

  /**
   * @param {string} productUrl
   */
  renderProductDetails (productUrl) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      if (!variant) {
        this.updateAvailability(false, window.theme.strings.unavailable)

        this.loading = false

        return
      }

      this.updateOptionValues(html)
      this.updateURL(productUrl, variant?.id)
      this.updateVariantInputs(variant?.id)
      this.updateWishlistTrigger(variant?.id)

      /**
       * @callback PostReplaceCallback
       * @param {Element} currentElement
       * @param {Element} newElement
       */

      /**
       * @param {string} selector
       * @param {PostReplaceCallback} [postReplaceCallback]
       */
      const updateCurrentPageFromFetchedData = (
        selector,
        postReplaceCallback,
      ) => {
        const newElement = html.querySelector(selector)
        const currentElements = getElements(selector)

        if (newElement && currentElements) {
          currentElements.forEach((currentElement) => {
            if (currentElement.innerHTML !== newElement.innerHTML) {
              currentElement.innerHTML = newElement.innerHTML
            }
          })

          if (typeof postReplaceCallback === 'function') {
            postReplaceCallback(currentElements, newElement)
          }
        }
      }

      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.media}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.price}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.stock}`,
      )

      const productIsAvailable = !html
        .querySelector('product-form button[type=submit]')
        ?.hasAttribute('disabled')

      const priceString = html.querySelector(selectors.price)?.innerHTML

      this.updateAvailability(productIsAvailable, '', variant, priceString)

      eventBus.dispatchEvent(EVENTS.PRODUCT.OPTION_CHANGED, {
        sectionId: this.sectionId,
        html,
        variant,
      })

      this.loading = false
    }
  }

  updateAvailability (productIsAvailable, text, variant, priceString) {
    const addToCartPanelEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartPanel}`,
    )
    const addToCartEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )
    addToCartPanelEls.forEach((addToCartPanelEl) => {
      addToCartPanelEl.setAttribute('aria-hidden', !productIsAvailable)
    })
    addToCartEls.forEach((addToCartEl) => {
      addToCartEl.toggleAttribute('disabled', !productIsAvailable)

      const buttonText =
        text || productIsAvailable
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut

      addToCartEl.innerHTML = `
        <button-text class="btn-Button_Text">${buttonText}</button-text>

        <div class="prd-Form_Price">
          ${priceString}
        </div>

        <div class="btn-Button_Icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" aria-hidden="true">
            <path stroke="currentColor" d="M7 8V6a3 3 0 0 1 6 0v2"/>
            <path stroke="currentColor" d="M6.41 5.5h7.18a1.5 1.5 0 0 1 1.488 1.312l1.14 9A1.5 1.5 0 0 1 14.73 17.5H5.27a1.5 1.5 0 0 1-1.489-1.688l1.14-9A1.5 1.5 0 0 1 6.41 5.5Z"/>
          </svg>
        </div>`
    })

    const backInStockPanelEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockPanel}`,
    )
    const backInStockFormEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockForm}`,
    )
    backInStockPanelEls.forEach((backInStockPanelEl) => {
      backInStockPanelEl.setAttribute('aria-hidden', productIsAvailable)
    })
    backInStockFormEls.forEach((backInStockFormEl) => {
      backInStockFormEl.toggleAttribute('disabled', productIsAvailable)
      backInStockFormEl.setAttribute('data-variant-id', variant?.id)
    })
  }

  /**
   * @param {Document} html
   */
  updateOptionValues (html) {
    const productOptions = html.querySelector(
      `${this.productOptions.tagName.toLowerCase()}`,
    )

    if (productOptions) {
      gracefullyReplaceNode(
        this.productOptions,
        productOptions,
        this.preReplaceProductOptionsCallbacks,
      )
    }
  }

  /**
   * @param {string} url
   * @param {string} [variantId]
   */
  updateURL (url, variantId) {
    if (this.dataset.updateUrl === 'false') return

    const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`

    window.history.replaceState({}, '', newUrl)

    const shareButtons = getElements('share-button')

    if (!shareButtons) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(newUrl)
    })
  }

  /**
   * @param {string} [variantId]
   */
  updateVariantInputs (variantId) {
    if (!variantId) return

    const input = this.productForm.querySelector('input[name="id"]')
    if (!input) return

    input.value = variantId
    input.dispatchEvent(new Event('change', { bubbles: true }))

    if (!this.externalEls['find-store']?.exists) return

    this.externalEls['find-store'].element.dataset.productUrl =
      this.externalEls['find-store'].element.dataset.productUrl.replace(
        /([?&]variant=)\d+/,
        `$1${variantId}`,
      )
  }

  /**
   * @param {string} [variantId]
   */
  updateWishlistTrigger (variantId) {
    if (!variantId) return

    const triggers = getElements(selectors.wishlistTrigger)

    triggers.forEach((el) => {
      el.setAttribute('data-variant-id', variantId)
    })

    document.dispatchEvent(new CustomEvent('checkIsInWishlist'))
  }

  /**
   * @param {string} url
   * @param {string[]} optionValues
   * @param {boolean} shouldFetchFullPage
   */
  buildRequestUrlWithParams (url, optionValues, shouldFetchFullPage = false) {
    const params = []

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  /**
   * @param {Document} productInfoNode
   *
   * @returns {ShopifyVariant | null}
   */
  getSelectedVariant (productInfoNode) {
    const selectedVariantDataEl = productInfoNode.querySelector(
      `${this.productOptions.tagName.toLowerCase()} [${this.productOptions._elIdentifier}="selected-variant"]`,
    )
    if (!selectedVariantDataEl) return null

    return JSON.parse(selectedVariantDataEl.innerHTML)
  }

  get productForm () {
    return this.els.form.element
  }

  get productInfoId () {
    return this.dataset.productInfoId
  }

  get productOptions () {
    return this.els.options.element
  }

  get sectionId () {
    return this.dataset.originalSection || this.dataset.section
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

class ProductInfoCache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  has (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

const cache = new ProductInfoCache()

/**
 * @param {string} requestUrl
 * @param {AbortSignal} abortSignal
 */
async function fetchProductData (requestUrl, abortSignal = undefined) {
  if (cache.has(requestUrl)) {
    return cache.get(requestUrl)
  }

  const response = await fetch(requestUrl, { signal: abortSignal })
  const text = await response.text()

  cache.set(requestUrl, text)

  return text
}
